import { motion } from 'framer-motion';
import { MapPin, Phone, Clock, Sparkles } from 'lucide-react';

const LocationsPage = () => {
  return (
    <div className="pt-24 pb-32 bg-cream min-h-screen">
      <div className="max-w-7xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-24"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 border border-gold/20 mb-6 text-gold font-bold text-[10px] uppercase tracking-[0.3em]">
            <MapPin size={14} />
            The Sanctuary
          </div>
          <h1 className="text-6xl md:text-9xl font-serif font-bold text-dark tracking-tighter mb-8 italic">
            Visit <br /><span className="text-gold not-italic">Charlotte</span>
          </h1>
          <p className="text-xl text-gray-500 max-w-2xl mx-auto font-light leading-relaxed">
            Our boutique sanctuary is located in the heart of Charlotte, NC. Discover the place where mastery meets tradition.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-10"
          >
            <div className="bg-white p-12 rounded-[50px] shadow-platinum border border-gold/10 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gold/5 rounded-bl-[100px]"></div>

              <h3 className="text-4xl font-serif font-bold text-dark mb-10 tracking-tight">Charlotte Flagship</h3>

              <div className="space-y-8">
                <div className="flex items-start gap-6 group">
                  <div className="p-4 rounded-2xl bg-cream text-gold group-hover:bg-gold group-hover:text-white transition-all duration-500">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase font-black text-gold/60 tracking-widest mb-1">Our Address</p>
                    <p className="text-xl font-bold text-dark">Charlotte, NC 28213</p>
                    <p className="text-gray-400 text-sm">Serving the entire Metrolina area.</p>
                  </div>
                </div>

                <div className="flex items-start gap-6 group">
                  <div className="p-4 rounded-2xl bg-cream text-gold group-hover:bg-gold group-hover:text-white transition-all duration-500">
                    <Clock size={24} />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase font-black text-gold/60 tracking-widest mb-1">Business Hours</p>
                    <p className="text-xl font-bold text-dark">Mon - Sat: 9:00 AM - 7:00 PM</p>
                    <p className="text-gray-400 text-sm">Appointments only on Sundays.</p>
                  </div>
                </div>

                <div className="flex items-start gap-6 group">
                  <div className="p-4 rounded-2xl bg-cream text-gold group-hover:bg-gold group-hover:text-white transition-all duration-500">
                    <Phone size={24} />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase font-black text-gold/60 tracking-widest mb-1">Contact Details</p>
                    <p className="text-xl font-bold text-dark">(704) 641-7227</p>
                    <p className="text-gray-400 text-sm">Call/Text for instant availability.</p>
                  </div>
                </div>
              </div>

              <div className="mt-12 pt-12 border-t border-gray-50 flex items-center justify-between">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-dark text-gold px-10 py-5 rounded-full font-bold uppercase tracking-widest text-[10px] shadow-xl flex items-center gap-3"
                >
                  Call for Directions <Phone size={14} />
                </motion.button>
                <div className="flex items-center gap-2 text-gold/40">
                  <Sparkles size={12} />
                  <span className="text-[9px] uppercase font-bold tracking-widest">Premium Site</span>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative h-[600px] rounded-[60px] overflow-hidden shadow-2xl border-4 border-white"
          >
            {/* Simple visual representation of a map since I cannot embed a live one easily without keys */}
            <div className="absolute inset-0 bg-dark/10">
              <img
                src="https://images.unsplash.com/photo-1526778548025-fa2f459cd5c1?w=1000&h=1200&fit=crop"
                alt="Charlotte NC Area"
                className="w-full h-full object-cover opacity-80"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-dark/60 via-transparent to-transparent"></div>
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                <div className="relative">
                  <div className="absolute inset-0 bg-gold rounded-full scale-[3] animate-ping-slow opacity-20"></div>
                  <div className="relative p-6 rounded-full bg-gold shadow-[0_0_40px_rgba(212,175,55,0.6)] border-4 border-white">
                    <MapPin size={32} className="text-dark fill-dark/20" />
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute bottom-10 left-10 right-10 glass p-8 rounded-[30px] flex items-center justify-between">
              <p className="text-dark font-serif font-bold italic tracking-tight underline cursor-pointer">Open Mobile Map</p>
              <div className="flex gap-2">
                {[...Array(3)].map((_, i) => <div key={i} className="w-2 h-2 rounded-full bg-gold"></div>)}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default LocationsPage;
